'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useChart } from '@/contexts/chart-context';
import { OIL_NAME_COLORS } from '@/constants/fuel-colors';
import HighchartsReact from 'highcharts-react-official';
import { useCallback, useEffect, useRef, useState } from 'react';

// Highcharts initialization
const initializeHighcharts = (Highcharts: any) => {
  if (!Highcharts) return;

  Highcharts.setOptions({
    lang: {
      thousandsSep: ',',
      noData: '표시될 데이터가 없습니다.',
    },
  });
};

interface PieData {
  name: string;
  y: number;
  amt: number;
  qty: number;
  cnt: number;
  color?: string;
}

const WJPNC_COLORS: { [key: string]: string } = {
  ...OIL_NAME_COLORS,
  // 판매구분 색상
  신용카드: '#8085e9',
  현금: '#f15c80',
  기타: '#e4d354',
};

interface PieChartsData {
  oilData: Array<{
    name: string;
    amt: number;
    qty: number;
    cnt: number;
  }>;
  saleTypeData: Array<{
    name: string;
    amt: number;
    qty: number;
    cnt: number;
  }>;
}

interface PieChartsProps {
  pieData?: PieChartsData;
}

export function PieCharts({ pieData }: PieChartsProps) {
  const { Highcharts, isHighchartsLoaded } = useChart();
  const [oilChartOptions, setOilChartOptions] = useState<any>({});
  const [saleTypeChartOptions, setSaleTypeChartOptions] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const oilChartRef = useRef<HighchartsReact.RefObject>(null);
  const saleTypeChartRef = useRef<HighchartsReact.RefObject>(null);

  const createPieChartOptions = (data: PieData[], title: string) => {
    const options: any = {
      chart: {
        plotBackgroundColor: undefined,
        plotBorderWidth: undefined,
        plotShadow: false,
        type: 'pie',
      },
      title: {
        text: '',
      },
      tooltip: {
        headerFormat: '',
        pointFormat:
          '<span style="color:{point.color}">●</span> <b>{point.name}</b>: {point.percentage:.1f}%<br/>{point.amt:,.0f} ₩<br/>{point.qty:.2f}<br/>{point.cnt} 건',
      },
      accessibility: {
        point: {
          valueSuffix: '%',
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format:
              '<b>{point.name}</b>: {point.percentage:.1f}%<br/>{point.amt:,.0f} ₩',
          },
        },
      },
      series: [
        {
          name: title,
          type: 'pie',
          // @ts-ignore
          colorByPoint: true,
          data: data,
        },
      ],
      credits: {
        enabled: false,
      },
    };

    return options;
  };

  const loadPieChartsData = useCallback(async () => {
    if (!isHighchartsLoaded || !Highcharts) return;

    try {
      setIsLoading(true);

      let oilData = pieData?.oilData || [];
      let saleTypeData = pieData?.saleTypeData || [];

      // Fallback to empty data if no pie data provided
      if (!pieData) {
        oilData = [];
        saleTypeData = [];
      }

      // Calculate percentages and add colors for oil data
      const totalOilQty = oilData.reduce((sum, item) => sum + item.qty, 0);
      const oilPieData: PieData[] =
        totalOilQty > 0
          ? oilData.map(item => ({
              name: item.name,
              y: (item.qty / totalOilQty) * 100,
              amt: item.amt,
              qty: item.qty,
              cnt: item.cnt,
              color: WJPNC_COLORS[item.name] || undefined,
            }))
          : [];

      // Calculate percentages and add colors for sale type data
      const totalSaleTypeQty = saleTypeData.reduce(
        (sum, item) => sum + item.qty,
        0
      );
      const saleTypePieData: PieData[] =
        totalSaleTypeQty > 0
          ? saleTypeData.map(item => ({
              name: item.name,
              y: (item.qty / totalSaleTypeQty) * 100,
              amt: item.amt,
              qty: item.qty,
              cnt: item.cnt,
              color: WJPNC_COLORS[item.name] || undefined,
            }))
          : [];

      setOilChartOptions(createPieChartOptions(oilPieData, '유종'));
      setSaleTypeChartOptions(
        createPieChartOptions(saleTypePieData, '판매구분')
      );
    } catch (error) {
      console.error('Failed to load pie chart data:', error);

      // Error fallback
      const emptyOptions = createPieChartOptions([], '');
      setOilChartOptions(emptyOptions);
      setSaleTypeChartOptions(emptyOptions);
    } finally {
      setIsLoading(false);
    }
  }, [isHighchartsLoaded, Highcharts, pieData]);

  useEffect(() => {
    if (isHighchartsLoaded && Highcharts) {
      initializeHighcharts(Highcharts);
    }
  }, [isHighchartsLoaded, Highcharts]);

  useEffect(() => {
    if (isHighchartsLoaded && Highcharts) {
      loadPieChartsData();
    }
  }, [loadPieChartsData, isHighchartsLoaded, Highcharts]);

  useEffect(() => {
    // Dashboard refresh event listener
    const handleRefresh = () => {
      if (isHighchartsLoaded && Highcharts) {
        loadPieChartsData();
      }
    };

    window.addEventListener('dashboard-refresh', handleRefresh);
    return () => window.removeEventListener('dashboard-refresh', handleRefresh);
  }, [loadPieChartsData, isHighchartsLoaded, Highcharts]);

  if (!isHighchartsLoaded || isLoading) {
    return (
      <div className='space-y-6'>
        <Card>
          <CardHeader>
            <CardTitle>유종 판매 비율</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='flex items-center justify-center'>
              <div className='text-gray-500'>
                {!isHighchartsLoaded
                  ? 'Highcharts 초기화 중...'
                  : '데이터 로딩 중...'}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>판매구분 비율</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='flex items-center justify-center'>
              <div className='text-gray-500'>
                {!isHighchartsLoaded
                  ? 'Highcharts 초기화 중...'
                  : '데이터 로딩 중...'}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle>유종 판매 비율</CardTitle>
        </CardHeader>
        <CardContent>
          <HighchartsReact
            ref={oilChartRef}
            highcharts={Highcharts}
            options={oilChartOptions}
            containerProps={{ style: { height: '100%' } }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>판매구분 비율</CardTitle>
        </CardHeader>
        <CardContent>
          <HighchartsReact
            ref={saleTypeChartRef}
            highcharts={Highcharts}
            options={saleTypeChartOptions}
            containerProps={{ style: { height: '100%' } }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
